// Non-destructive profile persistence.
//
// saveProfile() used to setDoc() the whole cogniprints/{userId} document, so any
// diagnostic run replaced everything that was there. A paid learner re-taking the
// QUICK diagnostic therefore lost all three of their paid dimensions — the quick
// payload carries them as null, and null overwrote the real scores.
//
// mergeQuickIntoFull() in cogniprint-scoring.ts was written for roughly this job
// and then never called from anywhere. This replaces it.
//
// The rule, in one line: evidence never loses to the absence of evidence.
// See docs/intent/001-diagnostic-redesign/plan.md A3.

import type { CogniPrintProfile, DiagnosticDimensionKey } from '../types/cogniprint';
import { PAID_DIMENSION_KEYS } from '../types/cogniprint';
import { isAssessed } from './profile-validation';
import { calculateOverallConfidence } from './cogniprint-scoring';

type PaidKey = 'infoDensity' | 'motivation' | 'viewpointRange';

/**
 * Merge a freshly-built profile over whatever is already stored.
 *
 * - An assessed incoming dimension always wins, including when it lowers a score.
 *   A learner must be able to correct their own profile.
 * - An unassessed incoming dimension (null / `not_assessed` / `defaulted`) never
 *   clears an assessed stored one.
 * - `diagnosticType` only ever ratchets up: a quick re-take does not demote a
 *   learner who has completed the full diagnostic.
 */
export function mergeProfileForSave(
  incoming: CogniPrintProfile,
  existing: CogniPrintProfile | null,
): CogniPrintProfile {
  if (!existing) return incoming;

  const merged: CogniPrintProfile = { ...incoming };
  const provenance: NonNullable<CogniPrintProfile['dimensionProvenance']> = {
    ...(existing.dimensionProvenance ?? {}),
    ...(incoming.dimensionProvenance ?? {}),
  };

  for (const key of PAID_DIMENSION_KEYS) {
    const paidKey = key as PaidKey;
    const incomingAssessed = isAssessed(incoming, key as DiagnosticDimensionKey);
    const existingAssessed = isAssessed(existing, key as DiagnosticDimensionKey);

    if (!incomingAssessed && existingAssessed && existing[paidKey] != null) {
      // Carry the stored dimension AND the provenance that goes with it.
      (merged as unknown as Record<string, unknown>)[paidKey] = existing[paidKey];
      provenance[key as DiagnosticDimensionKey] =
        existing.dimensionProvenance?.[key as DiagnosticDimensionKey] ?? 'inferred';
    }
  }

  merged.dimensionProvenance = provenance;

  // A quick re-take must not demote a learner who has completed the full diagnostic.
  merged.diagnosticType =
    existing.diagnosticType === 'full' ? 'full' : incoming.diagnosticType;

  // Preserve the original creation date; this is the same learner's profile.
  merged.createdAt = existing.createdAt ?? incoming.createdAt;

  // Carrying a stored dimension back in changes the confidence set it is drawn from.
  merged.overallConfidence = calculateOverallConfidence(merged);

  return merged;
}
